import { useMemo } from 'react';
import { type CombinedError, useQuery } from 'urql';

import {
  DATE_SELECTION_LIST_QUERY,
  type RefInfo,
  type WeekRefInfo,
} from '@revelio/data-access';

const buildRefs = (dates: RefInfo[] | WeekRefInfo[]): Map<number, string> => {
  const refs = new Map<number, string>();
  for (const { id, shortName } of dates) {
    const numericId = Number(id);
    if (Number.isNaN(numericId) || !shortName) continue;
    refs.set(numericId, shortName);
  }
  return refs;
};

export const useWeekRefs = ({
  pause = false,
}: {
  pause?: boolean;
} = {}): {
  weekRefs: Map<number, string>;
  isLoading: boolean;
  error: CombinedError | undefined;
} => {
  const [{ data, fetching, error }] = useQuery({
    query: DATE_SELECTION_LIST_QUERY,
    pause,
  });

  const weekRefs = useMemo(() => {
    const week = data?.selectionList?.week ?? [];
    const entries = week.filter(
      (w): w is WeekRefInfo =>
        w != null && typeof w.id === 'string' && typeof w.shortName === 'string'
    );
    return buildRefs(entries);
  }, [data]);

  return { weekRefs, isLoading: fetching, error };
};

export const useMonthRefs = () => {
  const [{ data, fetching, error }] = useQuery({
    query: DATE_SELECTION_LIST_QUERY,
  });

  const monthRefs = useMemo(() => {
    const months = data?.selectionList?.month ?? [];
    const entries = months.filter(
      (m): m is RefInfo =>
        m != null && typeof m.id === 'string' && typeof m.shortName === 'string'
    );
    return buildRefs(entries);
  }, [data]);

  return { monthRefs, isLoading: fetching, error };
};
